import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, XCircle, Share2, Play } from 'lucide-react';
import { AvatarCircle } from '@/components/avatar-circle';
import { buildChallengeUrl } from '@/lib/challenges/challenge-api';
import { formatRelativeDate } from './format';
import type { ChallengeRow } from './types';

export function ChallengeCard({
  data,
  authorName,
  authorAvatar,
  authorTutorId,
  viewerId,
}: {
  data: ChallengeRow;
  authorName: string;
  authorAvatar: string | null | undefined;
  authorTutorId?: string | null;
  viewerId?: string | null;
}) {
  const self = !!viewerId && viewerId === data.user_id;
  const name = self ? 'Tu' : authorName;
  const verb = self ? ' as lancé un défi' : ' a lancé un défi';
  const [copied, setCopied] = useState(false);
  const hasAnswers = data.total_attempts > 0;
  const successPct = hasAnswers
    ? Math.round((data.total_success / data.total_attempts) * 100)
    : null;
  const failPct = successPct !== null ? 100 - successPct : null;
  const hasThumbnail = !!data.thumbnail_url;

  async function handleShare() {
    const url = buildChallengeUrl(data.challenge_slug);
    if (typeof navigator.share === 'function') {
      await navigator.share({ title: data.title, url });
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <div className="jr-card flex w-full flex-col overflow-hidden rounded-2xl bg-white">
      <div className="flex">
        <div className="flex grow flex-col">
          <div className={['relative flex grow items-end'].join(' ')}>
            <div className="absolute inset-0" />

            <div className="relative w-full space-y-1 px-4 pb-4 pt-8">
              <div className="flex items-center gap-2">
                {authorTutorId ? (
                  <Link
                    to="/tutors/$tutorId"
                    params={{ tutorId: authorTutorId }}
                    className="flex items-center gap-2"
                  >
                    <AvatarCircle
                      name={authorName}
                      src={authorAvatar}
                      size="sm"
                      className="ring-2 ring-white/40"
                    />
                    <span className="font-sans text-xs">
                      <span className="text-ink-900 font-semibold hover:underline">{name}</span>
                      <span className="text-ink-500">{verb}</span>
                    </span>
                  </Link>
                ) : (
                  <>
                    <AvatarCircle
                      name={authorName}
                      src={authorAvatar}
                      size="sm"
                      className="ring-2 ring-white/40"
                    />
                    <span className="font-sans text-xs">
                      <span className="text-ink-900 font-semibold">{name}</span>
                      <span className="text-ink-500">{verb}</span>
                    </span>
                  </>
                )}
                <span className="text-ink-400 font-sans text-[11px]">·</span>
                <span className="text-ink-500 font-sans text-[11px]">
                  {formatRelativeDate(data.created_at)}
                </span>
              </div>
              <p className="font-display text-ink-900 text-sm font-extrabold leading-snug drop-shadow">
                {data.title}
              </p>
              {data.exercises?.title && (
                <p className="text-ink-600 font-sans text-xs leading-tight">
                  {data.exercises.title}
                </p>
              )}
            </div>
          </div>

          <div className="px-4 pb-4 pt-1">
            <div className="space-y-2">
              {hasAnswers ? (
                <>
                  <div className="flex items-center justify-between font-sans text-xs">
                    <span className="text-ink-500 font-semibold">
                      {data.total_attempts} réponse{data.total_attempts > 1 ? 's' : ''}
                    </span>
                    <span className="text-ink-400">
                      {data.total_views} vue{data.total_views > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex h-4 overflow-hidden rounded-full">
                    <div
                      className="flex items-center justify-center bg-green-400 font-sans text-[10px] font-bold text-white"
                      style={{ width: `${successPct}%` }}
                    >
                      {(successPct ?? 0) >= 25 ? `${successPct}%` : ''}
                    </div>
                    <div
                      className="flex items-center justify-center bg-red-300 font-sans text-[10px] font-bold text-white"
                      style={{ width: `${failPct}%` }}
                    >
                      {(failPct ?? 0) >= 25 ? `${failPct}%` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1 font-sans text-[11px]">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      <span className="text-ink-600">{successPct}% correctes</span>
                    </span>
                    <span className="flex items-center gap-1 font-sans text-[11px]">
                      <XCircle className="h-3 w-3 text-red-400" />
                      <span className="text-ink-600">{failPct}% incorrectes</span>
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-ink-400 font-sans text-xs">Pas encore de réponses</p>
              )}
            </div>
          </div>
        </div>
        <div
          className={[
            'aspect-9/16 bg-linear-to-t relative flex h-full w-40 items-end from-black/70 via-black/20 to-transparent',
            hasThumbnail ? '' : 'bg-linear-to-br from-primary-200 to-primary-400',
          ].join(' ')}
          style={
            hasThumbnail
              ? {
                  backgroundImage: `url(${data.thumbnail_url})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined
          }
        >
          <a
            href={buildChallengeUrl(data.challenge_slug)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Lancer le défi : ${data.title}`}
            className="absolute inset-0 flex items-center justify-center transition-all active:scale-[0.96]"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#ffffff]/90 shadow-lg backdrop-blur-sm">
              <Play className="h-6 w-6 translate-x-0.5 fill-black text-black" />
            </span>
          </a>
        </div>
      </div>

      {/* Bouton plein cadre, sous le contenu + la vignette */}
      <div className="px-3 pb-3 pt-1">
        <button
          onClick={handleShare}
          className="flex min-h-9 w-full items-center justify-center gap-1.5 rounded-xl bg-[linear-gradient(180deg,#f7d068,#f4c64e)] py-2.5 font-sans text-xs font-bold text-[#0a2e57] shadow-[0_3px_0_#c79a26] transition-all hover:bg-[linear-gradient(180deg,#f4c64e,#e7b431)] active:translate-y-0.5 active:shadow-[0_1px_0_#c79a26]"
        >
          <Share2 className="h-3.5 w-3.5" />
          {copied ? 'Lien copié !' : 'Repartager ce défi'}
        </button>
      </div>
    </div>
  );
}
